import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay, endOfDay, isToday } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UndoIcon from '@mui/icons-material/Undo';
import { useSnackbar } from 'notistack';

import { getSales, getIncomeByCategory, getIncomeByPaymentMethod } from '../firebase/financeServices';
import { generateGLEntryPreviews, createDailyClosingGLTransaction, GLEntryPreview, DailyClosingData, reverseDailyClosing } from '../services/glDailyClosingService';
import { saveDailyClosing, getDailyClosingByDate, updateDailyClosingWithGLTransaction, DailyClosing as DailyClosingType } from '../firebase/dailyClosingServices';
import GLEntryPreviewDialog from '../components/GLEntryPreviewDialog';
import ReverseGLEntryDialog from '../components/ReverseGLEntryDialog';
import { getAuth } from 'firebase/auth';

// Import types directly from financeServices
import { SaleItem as FirebaseSaleItem, PaymentMethod } from '../firebase/financeServices';

// Define our own Sale interface that matches what we need
interface Sale {
  id?: string;
  saleNumber: string;
  items: FirebaseSaleItem[];
  subtotal: number;
  discount?: number;
  tax?: number;
  total: number;
  paymentMethod: PaymentMethod;
  paymentStatus?: 'pending' | 'paid' | 'partially_paid';
  status?: 'active' | 'voided' | 'draft';
  createdAt: any; // Timestamp from Firebase
  updatedAt?: any;
  customerDetails?: {
    name: string;
    email?: string;
    phone?: string;
    customerId?: string;
  };
  staffId?: string;
  staffName?: string;
  notes?: string;
}

// Helper function to format currency with RM
const formatCurrency = (amount: number): string => {
  return 'RM ' + new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function to format date from Firestore Timestamp
const formatFirestoreDate = (timestamp: any): string => {
  if (!timestamp) return 'N/A';
  
  try {
    // Check if it's a Firestore Timestamp with toDate method
    if (timestamp && typeof timestamp.toDate === 'function') {
      return format(timestamp.toDate(), 'dd/MM/yyyy HH:mm');
    }
    
    // Otherwise try to parse it as a Date
    if (timestamp instanceof Date) {
      return format(timestamp, 'dd/MM/yyyy HH:mm');
    }
    
    return 'N/A';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

const DailyClosing: React.FC = () => {
  const location = useLocation();
  
  // Parse date from URL query parameter if present
  const getInitialDate = () => {
    const queryParams = new URLSearchParams(location.search);
    const dateParam = queryParams.get('date');
    if (dateParam) {
      try {
        return new Date(dateParam);
      } catch (e) {
        console.error('Invalid date parameter:', e);
      }
    }
    return new Date();
  };
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(getInitialDate());
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [categorySales, setCategorySales] = useState<{[key: string]: number}>({});
  const [paymentMethodSales, setPaymentMethodSales] = useState<{[key: string]: number}>({});
  const [isClosingDialogOpen, setIsClosingDialogOpen] = useState<boolean>(false);
  const [cashDrawerAmount, setCashDrawerAmount] = useState<string>('');
  const [cashVariance, setCashVariance] = useState<number>(0);
  const [closingComplete, setClosingComplete] = useState<boolean>(false);
  const [closingNotes, setClosingNotes] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [savingClosing, setSavingClosing] = useState<boolean>(false);
  const [dailyClosingData, setDailyClosingData] = useState<DailyClosingType | null>(null);
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // GL Entry preview state
  const [glEntryPreviewOpen, setGlEntryPreviewOpen] = useState<boolean>(false);
  const [glEntryPreviews, setGlEntryPreviews] = useState<GLEntryPreview[]>([]);
  const [isSubmittingGLEntries, setIsSubmittingGLEntries] = useState<boolean>(false);
  const [hasGLEntries, setHasGLEntries] = useState<boolean>(false);
  const [isReversingClosing, setIsReversingClosing] = useState<boolean>(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState<boolean>(false);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Payment method display names
  const paymentMethodDisplayNames: { [key: string]: string } = {
    'cash': 'Cash',
    'card': 'Credit/Debit Card',
    'qr': 'QR Payment',
    'bank_transfer': 'Bank Transfer',
    'other': 'Other'
  };

  // Calculate totals for the day - excluding voided transactions
  const calculateDailySummary = (sales: Sale[]) => {
    // Filter out voided transactions for calculations
    const validSales = sales.filter(sale => sale.status !== 'voided');

    // Calculate totals (excluding voided transactions)
    const total = validSales.reduce((sum, sale) => sum + sale.total, 0);
    setTotalSales(total);

    // Calculate category totals with proportional discount (excluding voided transactions)
    const categoryTotals: {[key: string]: number} = {};
    validSales.forEach(sale => {
      // Calculate subtotal for this sale from items
      const saleSubtotal = sale.items.reduce((sum, item) => sum + item.total, 0);

      // Calculate category subtotals for this sale
      const saleCategoryTotals: {[key: string]: number} = {};
      sale.items.forEach(item => {
        const category = item.type === 'product' ? 'Products' : 'Services';
        saleCategoryTotals[category] = (saleCategoryTotals[category] || 0) + item.total;
      });

      // Apply proportional discount if exists
      const saleDiscount = sale.discount || 0;
      if (saleDiscount > 0 && saleSubtotal > 0) {
        // Distribute discount proportionally across categories
        Object.entries(saleCategoryTotals).forEach(([category, subtotal]) => {
          const categoryDiscountAmount = (subtotal / saleSubtotal) * saleDiscount;
          const categoryTotal = subtotal - categoryDiscountAmount;
          categoryTotals[category] = (categoryTotals[category] || 0) + categoryTotal;
        });
      } else {
        // No discount, add full amounts
        Object.entries(saleCategoryTotals).forEach(([category, subtotal]) => {
          categoryTotals[category] = (categoryTotals[category] || 0) + subtotal;
        });
      }
    });
    setCategorySales(categoryTotals);

    // Calculate payment method totals (excluding voided transactions)
    const methodTotals: {[key: string]: number} = {};
    validSales.forEach(sale => {
      const method = sale.paymentMethod;
      methodTotals[method] = (methodTotals[method] || 0) + sale.total;
    });
    setPaymentMethodSales(methodTotals);
  };

  useEffect(() => {
    if (selectedDate) {
      fetchDailySales(selectedDate);
      fetchDailyClosingStatus(selectedDate);
    }
  }, [selectedDate]);
  
  // Fetch daily closing status from Firestore
  const fetchDailyClosingStatus = async (date: Date) => {
    try {
      const closingData = await getDailyClosingByDate(date);
      
      if (closingData) {
        setDailyClosingData(closingData);
        setClosingComplete(closingData.isClosed);
        setClosingNotes(closingData.notes || '');
        
        // Check if GL entries have been generated
        setHasGLEntries(!!closingData.glTransactionId);
        
        if (closingData.cashDrawerAmount) {
          setCashDrawerAmount(closingData.cashDrawerAmount.toString());
        }
        if (closingData.cashVariance !== undefined) {
          setCashVariance(closingData.cashVariance);
        }
      } else {
        setDailyClosingData(null);
        setClosingComplete(false);
        setClosingNotes('');
        setHasGLEntries(false);
      }
    } catch (err) {
      console.error('Error fetching daily closing status:', err);
      // Don't show error to user, just log it
    }
  };

  const fetchDailySales = async (date: Date) => {
    setLoading(true);
    setError(null);
    try {
      const startDate = startOfDay(date);
      const endDate = endOfDay(date);
      
      // Get sales data from Firebase
      const salesData = await getSales(startDate, endDate);
      
      // Transform the data to match our Sale interface
      const transformedSales = salesData.map(sale => {
        // Create a properly typed Sale object
        const transformedSale: Sale = {
          ...sale,
          // Ensure createdAt property exists
          createdAt: sale.createdAt || Timestamp.now(),
          // Handle status correctly - only use valid values
          status: (sale.status as any) || 'active'
        };
        return transformedSale;
      });
      
      setSales(transformedSales);
      
      // Calculate daily summary
      calculateDailySummary(transformedSales);
      
    } catch (err) {
      console.error('Error fetching daily sales:', err);
      setError('Failed to load sales data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    // Reset closing state when date changes
    setClosingComplete(false);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClosingDialogOpen = () => {
    // Reset cash drawer amount when opening dialog
    setCashDrawerAmount(paymentMethodSales['cash']?.toString() || '0');
    setCashVariance(0);
    setIsClosingDialogOpen(true);
  };

  const handleClosingDialogClose = () => {
    setIsClosingDialogOpen(false);
  };

  const handleCashDrawerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCashDrawerAmount(value);
    
    // Calculate variance
    const expectedCash = paymentMethodSales['cash'] || 0;
    const actualCash = parseFloat(value) || 0;
    setCashVariance(actualCash - expectedCash);
  };

  const handleClosingNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClosingNotes(event.target.value);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCompleteClosing = async () => {
    if (!selectedDate) return;
    
    setSavingClosing(true);
    
    try {
      // Get current user
      const auth = getAuth();
      const user = auth.currentUser;
      
      // Prepare daily closing data
      const closingData: Omit<DailyClosingType, 'id' | 'createdAt' | 'updatedAt'> = {
        date: selectedDate,
        totalSales,
        transactionCount: sales.length,
        categorySales,
        paymentMethodSales,
        cashDrawerAmount: parseFloat(cashDrawerAmount) || 0,
        cashVariance,
        notes: closingNotes,
        isClosed: true,
        closedAt: new Date(),
        closedBy: user?.uid || 'unknown'
      };
      
      // Save to Firestore
      await saveDailyClosing(closingData);
      
      setClosingComplete(true);
      setIsClosingDialogOpen(false);
      
      // Show success message
      setSnackbarMessage('Daily closing completed successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Refresh daily closing status
      fetchDailyClosingStatus(selectedDate);
    } catch (error) {
      console.error('Error completing daily closing:', error);
      setSnackbarMessage('Failed to complete daily closing');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingClosing(false);
    }
  };
  
  // Handle opening GL entry preview dialog
  const handleOpenGLEntryPreview = () => {
    if (!selectedDate) {
      enqueueSnackbar('Please select a date first', { variant: 'error' });
      return;
    }
    
    if (totalSales <= 0) {
      enqueueSnackbar('No sales to generate GL entries for', { variant: 'error' });
      return;
    }
    
    try {
      // Prepare daily closing data
      const dailyClosingData: DailyClosingData = {
        date: selectedDate,
        totalSales,
        categorySales,
        paymentMethodSales,
        transactionCount: sales.length,
        notes: closingNotes
      };
      
      // Generate GL entry previews
      const entries = generateGLEntryPreviews(dailyClosingData);
      setGlEntryPreviews(entries);
      setGlEntryPreviewOpen(true);
    } catch (error) {
      console.error('Error generating GL entry previews:', error);
      enqueueSnackbar('Failed to generate GL entries', { variant: 'error' });
    }
  };
  
  // Handle closing GL entry preview dialog
  const handleCloseGLEntryPreview = () => {
    setGlEntryPreviewOpen(false);
  };
  
  // Handle confirming and posting GL entries
  const handleConfirmGLEntries = async () => {
    if (!selectedDate) return;
    
    setIsSubmittingGLEntries(true);
    
    try {
      // Prepare daily closing data
      const glData: DailyClosingData = {
        date: selectedDate,
        totalSales,
        categorySales,
        paymentMethodSales,
        transactionCount: sales.length,
        notes: closingNotes
      };
      
      // Create GL transaction
      const transactionId = await createDailyClosingGLTransaction(glData, glEntryPreviews);
      
      // Update daily closing with GL transaction ID
      await updateDailyClosingWithGLTransaction(selectedDate, transactionId);
      
      // If not already closed, save the daily closing as closed
      if (!closingComplete) {
        // Get current user
        const auth = getAuth();
        const user = auth.currentUser;
        
        // Prepare daily closing data
        const closingData: Omit<DailyClosingType, 'id' | 'createdAt' | 'updatedAt'> = {
          date: selectedDate,
          totalSales,
          transactionCount: sales.length,
          categorySales,
          paymentMethodSales,
          cashDrawerAmount: parseFloat(cashDrawerAmount) || 0,
          cashVariance,
          notes: closingNotes,
          glTransactionId: transactionId,
          isClosed: true,
          closedAt: new Date(),
          closedBy: user?.uid || 'unknown'
        };
        
        // Save to Firestore
        await saveDailyClosing(closingData);
        setClosingComplete(true);
      }
      
      // Close dialog and show success message
      setGlEntryPreviewOpen(false);
      enqueueSnackbar('GL entries posted successfully', { variant: 'success' });
      
      // Refresh daily closing status
      fetchDailyClosingStatus(selectedDate);
    } catch (error) {
      console.error('Error posting GL entries:', error);
      enqueueSnackbar(`Failed to post GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsSubmittingGLEntries(false);
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleOpenReverseDialog = () => {
    if (!selectedDate || !dailyClosingData?.glTransactionId) return;
    setReverseDialogOpen(true);
  };

  const handleReverseClosing = async () => {
    if (!selectedDate) return;
    
    setIsReversingClosing(true);
    
    try {
      await reverseDailyClosing(selectedDate);
      
      // Refresh the daily closing status
      await fetchDailyClosingStatus(selectedDate);
      
      enqueueSnackbar('Daily closing reversed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error reversing daily closing:', error);
      enqueueSnackbar('Failed to reverse daily closing', { variant: 'error' });
      throw error; // Re-throw to propagate to the dialog
    } finally {
      setIsReversingClosing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Date Selection */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>Daily Closing</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
          </Typography>
          
          {/* Status Indicators */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip 
              icon={closingComplete ? <CheckCircleIcon /> : <ErrorIcon />}
              label={closingComplete ? 'Closed' : 'Not Closed'}
              color={closingComplete ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip 
              icon={hasGLEntries ? <CheckCircleIcon /> : <ErrorIcon />}
              label={hasGLEntries ? 'GL Posted' : 'GL Not Posted'}
              color={hasGLEntries ? 'success' : 'default'}
              variant="outlined"
            />
            
            {/* Reverse Closing Button - only show if day is closed and has GL entries */}
            {closingComplete && hasGLEntries && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleOpenReverseDialog}
                disabled={isReversingClosing}
                startIcon={<UndoIcon />}
              >
                Reverse Closing
              </Button>
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="primary"
            startIcon={<AccountBalanceIcon />}
            onClick={handleOpenGLEntryPreview}
            disabled={!selectedDate || totalSales <= 0 || hasGLEntries}
            title={hasGLEntries ? 'GL entries have already been generated' : 'Generate GL entries'}
          >
            {hasGLEntries ? 'GL Entries Generated' : 'Generate GL Entries'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />}
            onClick={handlePrintReport}
          >
            Print
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={handleDateChange}
            format="dd/MM/yyyy"
          />
        </LocalizationProvider>
        
        {!closingComplete && selectedDate && (
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={handleClosingDialogOpen}
          >
            Complete Closing
          </Button>
        )}
        
        {closingComplete && (
          <Chip 
            icon={<CheckCircleIcon />} 
            label="Closing Completed" 
            color="success"
            variant="outlined"
          />
        )}
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Sales Summary */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, my: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Total Sales</Typography>
              <Typography variant="h4">{formatCurrency(totalSales)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {sales.length} transactions
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>By Category</Typography>
              {Object.entries(categorySales).map(([category, amount]) => (
                <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">{category}</Typography>
                  <Typography variant="body1">{formatCurrency(amount)}</Typography>
                </Box>
              ))}
              {Object.keys(categorySales).length === 0 && (
                <Typography variant="body2" color="text.secondary">No sales data</Typography>
              )}
            </Paper>
            
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>By Payment Method</Typography>
              {Object.entries(paymentMethodSales).map(([method, amount]) => (
                <Box key={method} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">{paymentMethodDisplayNames[method] || method}</Typography>
                  <Typography variant="body1">{formatCurrency(amount)}</Typography>
                </Box>
              ))}
              {Object.keys(paymentMethodSales).length === 0 && (
                <Typography variant="body2" color="text.secondary">No payment data</Typography>
              )}
            </Paper>
          </Box>
          
          {/* Transactions Table */}
          <Paper sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              Transactions
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Sale #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((sale) => (
                      <TableRow key={sale.id} sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        ...(sale.status === 'voided' && { opacity: 0.6, backgroundColor: 'grey.100' })
                      }}>
                        <TableCell>{formatFirestoreDate(sale.createdAt)}</TableCell>
                        <TableCell>
                          {sale.saleNumber}
                          {sale.status === 'voided' && (
                            <Chip size="small" label="Voided" color="error" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{sale.customerDetails?.name || 'Walk-in'}</TableCell>
                        <TableCell>{sale.items.length}</TableCell>
                        <TableCell>{paymentMethodDisplayNames[sale.paymentMethod] || sale.paymentMethod}</TableCell>
                        <TableCell align="right">{formatCurrency(sale.total)}</TableCell>
                      </TableRow>
                    ))}
                  
                  {sales.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary">
                          No transactions found for this date
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination controls */}
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={sales.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows:"
              sx={{ '.MuiTablePagination-selectLabel': { marginBottom: 0 } }}
            />
          </Paper>
        </>
      )}
      
      {/* Closing Dialog */}
      <Dialog open={isClosingDialogOpen} onClose={handleClosingDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Complete Daily Closing</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Please verify the following information before completing the daily closing:
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>Sales Summary</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Total Sales: {formatCurrency(totalSales)}</Typography>
                <Typography variant="body2">Transactions: {sales.length}</Typography>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>By Category</Typography>
              {Object.entries(categorySales).map(([category, amount]) => (
                <Typography key={category} variant="body2">
                  {category}: {formatCurrency(amount)}
                </Typography>
              ))}
            </Box>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>By Payment Method</Typography>
              {Object.entries(paymentMethodSales).map(([method, amount]) => (
                <Typography key={method} variant="body2">
                  {paymentMethodDisplayNames[method] || method}: {formatCurrency(amount)}
                </Typography>
              ))}
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>Cash Reconciliation</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <TextField
                label="Cash in Drawer"
                fullWidth
                value={cashDrawerAmount}
                onChange={handleCashDrawerChange}
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>RM</Typography>,
                }}
                helperText="Enter the actual cash amount in the drawer"
                margin="normal"
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2">
                Expected Cash: {formatCurrency(paymentMethodSales['cash'] || 0)}
              </Typography>
              <Typography variant="body2" color={cashVariance < 0 ? 'error.main' : cashVariance > 0 ? 'success.main' : 'text.primary'}>
                Variance: {formatCurrency(cashVariance)} 
                {cashVariance < 0 ? ' (Shortage)' : cashVariance > 0 ? ' (Excess)' : ''}
              </Typography>
            </Box>
          </Box>
          
          <TextField
            label="Closing Notes"
            fullWidth
            multiline
            rows={4}
            value={closingNotes}
            onChange={handleClosingNotesChange}
            helperText="Add any notes about today's closing (optional)"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosingDialogClose}>Cancel</Button>
          <Button 
            onClick={handleCompleteClosing} 
            variant="contained" 
            color="primary"
            startIcon={savingClosing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            disabled={savingClosing}
          >
            {savingClosing ? 'Saving...' : 'Complete Closing'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* GL Entry Preview Dialog */}
      <GLEntryPreviewDialog
        open={glEntryPreviewOpen}
        onClose={handleCloseGLEntryPreview}
        onConfirm={handleConfirmGLEntries}
        entries={glEntryPreviews}
        isSubmitting={isSubmittingGLEntries}
        transactionDate={selectedDate || new Date()}
      />
      
      {/* Reverse GL Entry Dialog */}
      {dailyClosingData?.glTransactionId && selectedDate && (
        <ReverseGLEntryDialog
          open={reverseDialogOpen}
          onClose={() => setReverseDialogOpen(false)}
          onConfirm={handleReverseClosing}
          date={selectedDate}
          glTransactionId={dailyClosingData.glTransactionId}
        />
      )}
    </Box>
  );
};

export default DailyClosing;
